"use client";

import { useTranslations } from "next-intl";
import { AI_AGENT_ROLE, resolveAiName } from "@/lib/ai-assistant";
import { useVisitorChatRecentInboundQuery } from "@/lib/graphql/queries/generated/messaging.generated";
import { useConfigLocalizer } from "@/lib/localize-config-text";
import { stripHtml } from "@/lib/strip-html";
import type { HomeBlock, WidgetConfig, WidgetTab } from "@/lib/widget-config";
import { WexioMark } from "../../icons/wexio-mark";
import { formatRelativeTime } from "../helpers";

export function RecentMessageBlock({
  block,
  onOpenTab,
  onOpenThread,
  isDummy,
  chatId,
  config,
}: {
  block: Extract<HomeBlock, { kind: "recent-message" }>;
  onOpenTab: (tab: WidgetTab) => void;
  /** Open the conversation this card previews directly (its thread), rather
   *  than the thread list. Preferred when provided; falls back to the tab. */
  onOpenThread?: () => void;
  isDummy: boolean;
  chatId: string | null;
  config: WidgetConfig;
}) {
  const t = useTranslations("home");
  const localize = useConfigLocalizer();
  // A recent-message card is bound to a specific conversation, so tapping it
  // opens THAT thread, never the list.
  const open = () => (onOpenThread ? onOpenThread() : onOpenTab("messages"));

  // Live mode — fetch the visitor's latest inbound (operator/bot/AI)
  // message. Skip the query when there's no chat yet (anonymous visitor
  // hasn't authed) and hide the block to avoid the empty rectangle.
  const { data, loading } = useVisitorChatRecentInboundQuery({
    variables: { chatId: chatId ?? "" },
    skip: isDummy || !chatId,
    // cache-first via the apollo defaults — the shell-level SSE pump
    // writes new inbounds straight into this query's cache slot, so
    // there's no need to re-fetch on every block mount.
  });

  if (!isDummy) {
    if (!chatId || loading) return null;
    const msg = data?.visitorChatRecentInbound;
    if (!msg) return null;

    // AI / bot senders display as AI_ASSISTANT_NAME regardless of what the BE
    // labels them ("Bot", "WEB widget", an internal handle, …) — same
    // convention used in the conversation bubble + typing indicator.
    // Operator senders flow through unchanged. Avatar for AI/bot uses
    // the operator-configured `messenger.aiAssistantAvatar`; when
    // unset, render no avatar at all (no "B" monogram fallback for
    // bot — that read as broken).
    const isAi = msg.sender?.kind === "ai" || msg.sender?.kind === "bot";
    const displayName = isAi
      ? (resolveAiName(config.messenger?.aiAssistant) ?? AI_AGENT_ROLE)
      : msg.sender?.name?.trim() || t("recentMessageFromFallback");
    const avatarUrl = isAi
      ? (config.messenger?.aiAssistantAvatar ?? null)
      : (msg.sender?.avatar ?? null);

    return (
      <button
        type="button"
        onClick={open}
        className="flex w-full flex-col gap-3 rounded-wx-lg bg-wx-bg-elevated px-4 py-3.5 text-start transition-colors hover:bg-wx-bg-elevated-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
      >
        <p className="text-sm text-wx-fg-muted">{localize(block.title, t("recentMessageTitle"))}</p>
        <div className="flex items-start gap-3">
          {avatarUrl ? (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-wx-bg">
              {/* biome-ignore lint/performance/noImgElement: operator/sender avatar of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-9 w-9 rounded-full object-cover"
              />
            </span>
          ) : isAi ? // AI/bot with no operator-configured avatar → reserve no
          // gutter so the text reads flush, matching the
          // explicit-opt-in shape used elsewhere for AI avatars.
          null : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-wx-bg">
              <span className="text-sm font-semibold text-wx-fg-muted">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="truncate text-sm font-semibold text-wx-fg">{displayName}</p>
              <span className="shrink-0 text-xs text-wx-fg-subtle">
                {formatRelativeTime(msg.createdAt)}
              </span>
            </div>
            {/* A media message carries its text as `caption` (`text` is null),
                so fall back to it; a media message with NEITHER (a bare image /
                file, no caption) shows a generic "Attachment" label so the
                preview line isn't blank. */}
            <p className="mt-0.5 line-clamp-2 text-sm text-wx-fg-muted">
              {stripHtml(msg.text || msg.caption) || t("recentMessageAttachment")}
            </p>
          </div>
        </div>
      </button>
    );
  }

  // Demo mode — bundled sample. The sample author is the AI/bot, so mirror the
  // live branch: label it with the operator-configured assistant name (falls
  // back to the generic "from" label when the operator disabled the identity).
  const demoName = resolveAiName(config.messenger?.aiAssistant) ?? t("recentMessageFromFallback");
  const demoAvatar = config.messenger?.aiAssistantAvatar ?? null;
  return (
    <button
      type="button"
      onClick={open}
      className="flex w-full flex-col gap-3 rounded-wx-lg bg-wx-bg-elevated px-4 py-3.5 text-start transition-colors hover:bg-wx-bg-elevated-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
    >
      <p className="text-sm text-wx-fg-muted">{localize(block.title, t("recentMessageTitle"))}</p>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-wx-bg">
          {demoAvatar ? (
            // biome-ignore lint/performance/noImgElement: operator avatar of arbitrary host/dimensions; next/image needs known sizes + an allowlist
            <img src={demoAvatar} alt={demoName} className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <WexioMark size={36} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="truncate text-sm font-semibold text-wx-fg">{demoName}</p>
            <span className="shrink-0 text-xs text-wx-fg-subtle">{t("recentMessageAgo")}</span>
          </div>
          <p className="mt-0.5 line-clamp-2 text-sm text-wx-fg-muted">{t("recentMessageSample")}</p>
        </div>
      </div>
    </button>
  );
}
